/**
 * Domain Card Formatter
 * Formats domain card text for better readability
 */

/**
 * Format domain card description text with proper styling
 * Detects multi-ability cards and formats them with:
 * - Bold ability names
 * - Spacing between abilities
 * - Preserved line breaks within abilities
 *
 * @param {string} text - Raw domain card text
 * @returns {JSX.Element|string} Formatted text with styling
 */
export const formatDomainCardText = (text) => {
  if (!text) return text;

  // Check if this is a multi-ability card (has colons indicating ability names)
  const hasMultipleAbilities = text.includes(':');

  if (!hasMultipleAbilities) {
    // Single ability - return as-is with preserved whitespace
    return text;
  }

  // Multi-ability card - split and format each ability
  const lines = text.split('\n');
  const abilities = [];
  let currentAbility = null;

  lines.forEach((line) => {
    // Check if this line starts a new ability (has a colon)
    const abilityMatch = line.match(/^([^:]+):\s*(.*)$/);

    if (abilityMatch) {
      // Save previous ability if exists
      if (currentAbility) {
        abilities.push(currentAbility);
      }

      // Start new ability
      currentAbility = {
        name: abilityMatch[1].trim(),
        description: [abilityMatch[2]].filter(Boolean) // Only include if not empty
      };
    } else if (currentAbility && line.trim()) {
      // Add line to current ability description
      currentAbility.description.push(line);
    } else if (!currentAbility && line.trim()) {
      // Text before any ability name (intro text)
      abilities.push({
        name: null,
        description: [line]
      });
    }
  });

  // Don't forget the last ability
  if (currentAbility) {
    abilities.push(currentAbility);
  }

  return abilities;
};

/**
 * Check if text is multi-ability
 * @param {string} text - Domain card text
 * @returns {boolean}
 */
export const isMultiAbilityCard = (text) => {
  return text && text.includes(':');
};
